import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Mail, Phone, Lock, AlertCircle, CheckCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const IntentFormPage = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    speciality_id: "",
    membership_type: "",
    rci_registered: "no",
    rci_number: "",
    password: "",
    confirm_password: "",
  });
  
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const membershipTypes = [
    { value: "fellow", label: "Fellow Membership", international: false },
    { value: "professional", label: "Professional Membership", international: false },
    { value: "associate", label: "Associate Membership", international: false },
    { value: "student", label: "Student Membership", international: false },
    { value: "professional_international", label: "Professional International Membership", international: true },
    { value: "associate_international", label: "Associate International Membership", international: true },
  ];

  useEffect(() => {
    fetchSpecialities();
  }, []);

  const fetchSpecialities = async () => {
    try {
      const response = await axios.get(`${API}/admin/specialities`);
      setSpecialities(response.data);
    } catch (error) {
      console.error("Error fetching specialities:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.email || !formData.phone || !formData.speciality_id || 
        !formData.membership_type || !formData.password) {
      setError("Please fill in all required fields");
      return false;
    }
    
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    
    if (formData.password !== formData.confirm_password) {
      setError("Passwords do not match");
      return false;
    }
    
    if (formData.rci_registered === "yes" && !formData.rci_number) {
      setError("Please provide your RCI Registration Number");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError("");

    try {
      const payload = {
        email: formData.email,
        phone: formData.phone,
        speciality_id: formData.speciality_id,
        membership_type: formData.membership_type,
        rci_registered: formData.rci_registered === "yes",
        rci_number: formData.rci_registered === "yes" ? formData.rci_number : null,
        password: formData.password,
      };

      const response = await axios.post(`${API}/auth/intent`, payload);
      
      setSuccess(true);
      
      // Store email for verification page
      localStorage.setItem("verification_email", formData.email);
      localStorage.setItem("verification_phone", formData.phone);
      
      // Redirect to verification page after 2 seconds
      setTimeout(() => {
        navigate("/verify");
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-teal-700" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">
              Intent Submitted!
            </h2>
            <p className="text-slate-600 mb-4">
              We've sent verification links to your email and phone/WhatsApp. 
              Please verify both to continue your application.
            </p>
            <p className="text-sm text-slate-500">
              Redirecting to verification page...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="intent-form-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Become a CPSI Member
          </h1>
          <p className="text-slate-600">
            Start your membership journey with this simple form
          </p>
        </div>

        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-slate-900">
              Intent to Join
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Email Address *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 h-11"
                    data-testid="intent-email"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Phone / WhatsApp Number *
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="pl-10 h-11"
                    data-testid="intent-phone"
                  />
                </div>
              </div>

              {/* Speciality */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Speciality *
                </Label>
                <Select
                  value={formData.speciality_id}
                  onValueChange={(value) => handleSelectChange("speciality_id", value)}
                >
                  <SelectTrigger className="h-11" data-testid="intent-speciality">
                    <SelectValue placeholder="Select your speciality" />
                  </SelectTrigger>
                  <SelectContent>
                    {specialities.map((spec) => (
                      <SelectItem key={spec.id} value={spec.id}>
                        {spec.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Membership Type */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Type of Membership *
                </Label>
                <Select
                  value={formData.membership_type}
                  onValueChange={(value) => handleSelectChange("membership_type", value)}
                >
                  <SelectTrigger className="h-11" data-testid="intent-membership">
                    <SelectValue placeholder="Select membership type" />
                  </SelectTrigger>
                  <SelectContent>
                    {membershipTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* RCI Registration */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  RCI Registration *
                </Label>
                <RadioGroup
                  value={formData.rci_registered}
                  onValueChange={(value) => handleSelectChange("rci_registered", value)}
                  className="flex gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="rci-yes" data-testid="rci-yes" />
                    <Label htmlFor="rci-yes" className="font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="rci-no" data-testid="rci-no" />
                    <Label htmlFor="rci-no" className="font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* RCI Number (conditional) */}
              {formData.rci_registered === "yes" && (
                <div>
                  <Label htmlFor="rci_number" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    RCI Registration Number *
                  </Label>
                  <Input
                    id="rci_number"
                    name="rci_number"
                    placeholder="Enter your RCI number"
                    value={formData.rci_number}
                    onChange={handleChange}
                    className="h-11"
                    data-testid="intent-rci-number"
                  />
                </div>
              )}

              {/* Password */}
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Create Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 h-11"
                    data-testid="intent-password"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Confirm Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type="password"
                    placeholder="Re-enter password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    className="pl-10 h-11"
                    data-testid="intent-confirm-password"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-slate-900 text-white hover:bg-slate-800 h-11"
                disabled={loading}
                data-testid="intent-submit"
              >
                {loading ? "Submitting..." : "Submit Intent"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              Already have an account?{" "}
              <a href="/login" className="text-teal-700 hover:text-teal-800 font-medium">
                Login here
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntentFormPage;
